/**
 * Design System Demo Page
 * 
 * Showcases the semantic design system components including:
 * - MetricTile with all variants
 * - DataQCDetailTable with visual diffing
 * - Theme switching capabilities
 * - Font hierarchy (Inter for UI, JetBrains Mono for data)
 */
import React, { useState } from 'react';
import { MetricTile, MetricTileGrid } from '../components/MetricTile';
import { DataQCDetailTable, createQCTableColumns } from '../components/DataQCDetailTable';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  TrendingUp,
  Database,
  Zap,
  Clock,
} from 'lucide-react';

// Mock data for demonstration
const mockQCData = [
  {
    row_id: 1,
    column_name: 'email',
    original_value: 'john.doe@example',
    corrected_value: 'john.doe@example.com',
    confidence: 0.98,
    status: 'Valid',
  },
  {
    row_id: 2,
    column_name: 'phone',
    original_value: '555-1234',
    corrected_value: '(555) 123-4567',
    confidence: 0.92,
    status: 'Valid',
  },
  {
    row_id: 5,
    column_name: 'postal_code',
    original_value: '1234',
    corrected_value: '12345',
    confidence: 0.85,
    status: 'Valid',
  },
  {
    row_id: 8,
    column_name: 'date',
    original_value: '2024-13-01',
    corrected_value: '2024-01-13',
    confidence: 0.95,
    status: 'Valid',
  },
  {
    row_id: 10,
    column_name: 'status',
    original_value: 'activ',
    corrected_value: 'active',
    confidence: 0.99,
    status: 'Valid',
  },
];

const DesignSystemDemo: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.classList.toggle('dark');
  };

  return (
    <div className="min-h-screen bg-bg-base p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-4xl font-bold text-text-primary font-ui mb-2">
              Design System Demo
            </h2>
            <p className="text-text-secondary font-ui">
              Semantic components with adaptive light/dark mode support
            </p>
          </div>
          <button
            onClick={toggleTheme}
            className="px-4 py-2 rounded-lg bg-bg-elevated border border-border hover:bg-bg-hover transition-colors font-ui"
          >
            Toggle {theme === 'light' ? 'Dark' : 'Light'} Mode
          </button>
        </div>

        {/* Font Showcase */}
        <div className="grid grid-cols-2 gap-6">
          <div className="p-6 rounded-lg bg-bg-surface border border-border">
            <h3 className="text-lg font-semibold text-text-primary font-ui mb-3">
              UI Font: Inter
            </h3>
            <p className="text-sm text-text-secondary font-ui mb-2">
              Used for labels, descriptions, and UI elements
            </p>
            <div className="space-y-1">
              <p className="text-xs font-ui text-text-tertiary">Extra Small UI Text</p>
              <p className="text-sm font-ui text-text-secondary">Small UI Text</p>
              <p className="text-base font-ui text-text-primary">Base UI Text</p>
              <p className="text-lg font-ui font-semibold text-text-primary">Large UI Text</p>
            </div>
          </div>

          <div className="p-6 rounded-lg bg-bg-surface border border-border">
            <h3 className="text-lg font-semibold text-text-primary font-ui mb-3">
              Data Font: JetBrains Mono
            </h3>
            <p className="text-sm text-text-secondary font-ui mb-2">
              Used for data tables, metrics, and code
            </p>
            <div className="space-y-1">
              <p className="text-xs font-data text-text-tertiary">1234567890</p>
              <p className="text-sm font-data text-text-secondary">john.doe@example.com</p>
              <p className="text-base font-data text-text-primary">0.9876543210</p>
              <code className="text-sm font-data text-qc-success">SELECT * FROM table;</code>
            </div>
          </div>
        </div>

        {/* Metric Tiles - Default */}
        <div>
          <h2 className="text-2xl font-semibold text-text-primary font-ui mb-4">
            Metric Tiles - Default Variant
          </h2>
          <MetricTileGrid columns={4}>
            <MetricTile
              label="Total Jobs"
              value="1,234"
              description="All time"
              IconComponent={Database}
            />
            <MetricTile
              label="Processing Speed"
              value="45.2K"
              description="rows/sec"
              trend="+12.5%"
              IconComponent={Zap}
            />
            <MetricTile
              label="Avg. Response Time"
              value="245ms"
              description="Last 7 days"
              IconComponent={Clock}
            />
            <MetricTile
              label="Success Rate"
              value="98.7%"
              trend="↑ 2.1%"
              IconComponent={TrendingUp}
            />
          </MetricTileGrid>
        </div>

        {/* Metric Tiles - Semantic Variants */}
        <div>
          <h2 className="text-2xl font-semibold text-text-primary font-ui mb-4">
            Semantic Variants
          </h2>
          <MetricTileGrid columns={4}>
            <MetricTile
              label="Successful QC"
              value="856"
              variant="success"
              description="This month"
              IconComponent={CheckCircle2}
            />
            <MetricTile
              label="Warnings"
              value="42"
              variant="warning"
              description="Needs review"
              IconComponent={AlertTriangle}
            />
            <MetricTile
              label="Failed Jobs"
              value="8"
              variant="error"
              description="Last 24 hours"
              IconComponent={XCircle}
            />
            <MetricTile
              label="Processing"
              value="23"
              variant="info"
              description="Currently active"
              IconComponent={Info}
            />
          </MetricTileGrid>
        </div>

        {/* Color Palette */}
        <div>
          <h2 className="text-2xl font-semibold text-text-primary font-ui mb-4">
            Semantic Color System
          </h2>
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <div className="h-20 rounded-lg bg-qc-success flex items-center justify-center">
                <span className="text-white font-ui font-semibold">Success</span>
              </div>
              <div className="h-12 rounded qc-success-bg flex items-center justify-center">
                <span className="text-qc-success font-ui text-sm">Success BG (10%)</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="h-20 rounded-lg bg-qc-warning flex items-center justify-center">
                <span className="text-white font-ui font-semibold">Warning</span>
              </div>
              <div className="h-12 rounded qc-warning-bg flex items-center justify-center">
                <span className="text-qc-warning font-ui text-sm">Warning BG (10%)</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="h-20 rounded-lg bg-qc-error flex items-center justify-center">
                <span className="text-white font-ui font-semibold">Error</span>
              </div>
              <div className="h-12 rounded qc-error-bg flex items-center justify-center">
                <span className="text-qc-error font-ui text-sm">Error BG (10%)</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="h-20 rounded-lg bg-qc-info flex items-center justify-center">
                <span className="text-white font-ui font-semibold">Info</span>
              </div>
              <div className="h-12 rounded qc-info-bg flex items-center justify-center">
                <span className="text-qc-info font-ui text-sm">Info BG (10%)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Data QC Detail Table */}
        <div>
          <h2 className="text-2xl font-semibold text-text-primary font-ui mb-4">
            Data QC Detail Table
          </h2>
          <p className="text-text-secondary font-ui mb-4">
            Rows <strong className="text-qc-success">modified by AI</strong> have a subtle green
            background tint that remains visible in both light and dark modes.
          </p>
          <DataQCDetailTable
            data={mockQCData}
            columns={createQCTableColumns()}
            modifiedRowIds={[1, 2, 4]} // Rows at index 1, 2, 4 were modified
            highlightColumn="corrected_value"
            showRowNumbers
            stickyHeader
            maxHeight="400px"
          />
        </div>

        {/* Background Hierarchy */}
        <div>
          <h2 className="text-2xl font-semibold text-text-primary font-ui mb-4">
            Background Hierarchy
          </h2>
          <div className="space-y-3">
            <div className="p-4 bg-bg-base border border-border rounded">
              <span className="font-ui text-text-primary">bg-base - Base background</span>
            </div>
            <div className="p-4 bg-bg-surface border border-border rounded">
              <span className="font-ui text-text-primary">bg-surface - Cards and panels</span>
            </div>
            <div className="p-4 bg-bg-elevated border border-border rounded shadow-sm">
              <span className="font-ui text-text-primary">bg-elevated - Elevated surfaces</span>
            </div>
            <div className="p-4 bg-bg-subtle border border-border rounded">
              <span className="font-ui text-text-primary">bg-subtle - Subtle backgrounds</span>
            </div>
            <div className="p-4 bg-bg-hover border border-border rounded">
              <span className="font-ui text-text-primary">bg-hover - Hover states</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignSystemDemo;
